import styled from "@emotion/styled";
import { ArrowBackIos } from "@mui/icons-material";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  Paper as MuiPaper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router-dom";

import GlobalStyle from "./components/GlobalStyle";
import RefreshTokenBackdrop from "./components/backdrops/refreshTokenBackdrop/RefreshTokenBackdrop";
import MobileNavBar from "./components/mobileNavBar/MobileNavBar";
import Navbar from "./components/navbar/Navbar";
import PageHeader from "./components/pageheader/PageHeader";
import dashboardItems from "./components/sidebar/SideBarItems";
import Sidebar from "./components/sidebar/Sidebar";
import useNavigateTo from "./hooks/useNavigateTo";

const drawerWidth = 258;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const Paper = styled(MuiPaper)(spacing);

interface LayoutProps {
  authorized: boolean;
}

const Layout = (props: React.PropsWithChildren<LayoutProps>) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { navigateBack } = useNavigateTo();
  const routerLocation = useLocation();

  const { authorized } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  const isHomeScreen = routerLocation.pathname === "/home";
  const isDeliveriesScreen = routerLocation.pathname === "/deliveries";

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const backButton = () => {
    //we don't want to show the backButton on Dashboard or DeliveriesOverview
    if (
      isHomeScreen ||
      isDeliveriesScreen ||
      !window.history.state ||
      window.history.state.idx <= 0
    )
      return <></>;

    // only show the back button if there is last page to navigate to
    return (
      <div className="main-content-header-back-button-container">
        <IconButton
          className="main-content-header-back-button"
          onClick={() => navigateBack()}
        >
          <ArrowBackIos />
        </IconButton>
      </div>
    );
  };

  return (
    <div className="bp-root">
      {/* import of the used font */}
      <link rel="stylesheet" href="https://use.typekit.net/trt3hgk.css"></link>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems(t)}
            authorized={authorized}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={dashboardItems(t)}
            authorized={authorized}
          />
        </Box>
      </Drawer>
      <div className="app-content">
        <AppBar position="sticky" elevation={0}>
          <Navbar showAppName={false} onDrawerToggle={handleDrawerToggle} />
          <>
            {backButton()}
            <PageHeader inverse={false} isHomeScreen={isHomeScreen} />
          </>
        </AppBar>
        <Paper
          className={!isHomeScreen ? "main-content" : "main-content-homeScreen"}
          p={isLgUp ? 12 : 5}
        >
          {authorized && <Outlet />}
        </Paper>

        {/* only display the mobile app bar if the size is xs */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            display: { xs: "block", md: "none" },
            top: "auto",
            bottom: 0,
          }}
        >
          <MobileNavBar items={dashboardItems(t)} />
        </AppBar>
      </div>
      {!authorized && <RefreshTokenBackdrop />}
    </div>
  );
};

export default Layout;
